import React from 'react';
import { useSelector } from 'react-redux';
import { BasePageProps } from './PageTypes';
import { CommonPageId } from './PageTypes';
import { RootState } from '../Store/store';

export interface PageRenderProps extends BasePageProps {
  pageId: string | CommonPageId;
  navigateToPage?: (pageId: string | CommonPageId, navigationProps?: any) => void;
  navigationProps?: any; // Additional props passed during navigation
}

export type PageRenderer = (props: PageRenderProps) => React.ReactElement;

/**
 * Core page registry system.
 * This keeps the Common directory game-agnostic while allowing flexible page management.
 */
class PageRegistryCore {
  private registry: Map<string | CommonPageId, PageRenderer> = new Map();
  private commonPages: Map<CommonPageId, PageRenderer> = new Map();

  /**
   * Register a page renderer for a specific page ID.
   * Each game has its own registered pages.
   */
  registerPage(pageId: string | CommonPageId, renderer: PageRenderer): void {
    this.registry.set(pageId, renderer);
  }

  /**
   * Register multiple pages at once.
   * Convenient to register all of a game's pages during initialization.
   */
  registerPages(pages: Record<string, PageRenderer>): void {
    Object.entries(pages).forEach(([pageId, renderer]) => {
      this.registerPage(pageId, renderer);
    });
  }

  /**
   * Register a common page that can be used by multiple games.
   * These are stored separately to avoid conflicts.
   */
  registerCommonPage(pageId: CommonPageId, renderer: PageRenderer): void {
    this.commonPages.set(pageId, renderer);
    this.registry.set(pageId, renderer);
  }

  /**
   * Get a page renderer by its ID.
   * Returns null if the page is not registered.
   */
  getPageRenderer(pageId: string | CommonPageId): PageRenderer | null {
    return this.registry.get(pageId) || null;
  }

  /**
   * Check if a page is registered.
   */
  hasPage(pageId: string | CommonPageId): boolean {
    return this.registry.has(pageId);
  }

  /**
   * Get all registered page IDs.
   */
  getRegisteredPageIds(): (string | CommonPageId)[] {
    return Array.from(this.registry.keys());
  }

  /**
   * Clear all registered pages (useful for testing).
   */
  clear(): void {
    this.registry.clear();
    this.commonPages.clear();
  }

  /**
   * Unregister a specific page.
   */
  unregisterPage(pageId: string | CommonPageId): void {
    this.registry.delete(pageId);
    this.commonPages.delete(pageId as CommonPageId);
  }
}

// Export singleton instance
export const pageRegistry = new PageRegistryCore();

/**
 * Default fallback page component for unregistered pages.
 */
export const DefaultPageComponent = ({ onRequestClose, pageId }: PageRenderProps): React.ReactElement => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  
  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>Page: {pageId}</h2>
      <p>This page is not yet implemented.</p>
      {onRequestClose && currentGame && (
        <button onClick={onRequestClose} style={{ 
          padding: '10px 20px', 
          margin: '10px',
          backgroundColor: currentGame.colorPrimary,
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer'
        }}>
          Close
        </button>
      )}
    </div>
  );
};

/**
 * Convenience function to get a page renderer.
 * Returns DefaultPageComponent if page is not found.
 */
export const getPageComponent = (pageId: string | CommonPageId): PageRenderer => {
  return pageRegistry.getPageRenderer(pageId) || DefaultPageComponent;
};
